import { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { useCallbackOne as useStableCallback } from 'use-memo-one';
import { useAtomEffect } from '../../modernization-common/hooks/useAtomEffect';
import { type PubSubEvent, publishEventAtom, pubSubEventAtom } from './communication-atoms';
export type EventHandler<T = any> = (event: PubSubEvent<T>) => void;
export type SubscriptionOptions = {
  once?: boolean; // Unsubscribe after first event
};
export const usePubSub = () => {
  const publishEvent = useSetAtom(publishEventAtom);

  // Track active subscriptions
  const subscriptionsRef = useRef<Map<string, {
    eventTypes: string[];
    handler: EventHandler;
    options?: SubscriptionOptions;
  }>>(new Map());

  // Publish an event
  const publish = useCallback(<T = any,>(type: string, payload?: T) => {
    publishEvent({
      type,
      payload
    });
  }, [publishEvent]);

  // Subscribe to event types
  const subscribe = useCallback(<T = any,>(eventTypes: string | string[], handler: EventHandler<T>, options?: SubscriptionOptions): (() => void) => {
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    const subscriptionId = `${Date.now()}_${Math.random()}`;

    // Store subscription
    subscriptionsRef.current.set(subscriptionId, {
      eventTypes: types,
      handler: (handler as EventHandler),
      options
    });

    // Return unsubscribe function
    return () => {
      subscriptionsRef.current.delete(subscriptionId);
    };
  }, []);

  // Process current event against all subscriptions using useAtomEffect
  useAtomEffect(useStableCallback(get => {
    const currentEvent = get(pubSubEventAtom);
    if (!currentEvent) return;
    subscriptionsRef.current.forEach((subscription, subscriptionId) => {
      const {
        eventTypes,
        handler,
        options
      } = subscription;
      if (eventTypes.includes(currentEvent.type)) {
        handler(currentEvent);

        // Remove subscription if it's a one-time subscription
        if (options?.once) {
          subscriptionsRef.current.delete(subscriptionId);
        }
      }
    });
  }, []));

  // Cleanup all subscriptions on unmount
  useEffect(() => {
    const currentSubscriptions = subscriptionsRef.current;
    return () => {
      currentSubscriptions.clear();
    };
  }, []);

  // Unsubscribe from specific event types
  const unsubscribe = useCallback((eventTypes: string | string[]) => {
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    subscriptionsRef.current.forEach((subscription, subscriptionId) => {
      const hasMatchingType = subscription.eventTypes.some(type => types.includes(type));
      if (hasMatchingType) {
        subscriptionsRef.current.delete(subscriptionId);
      }
    });
  }, []);

  // Unsubscribe all
  const unsubscribeAll = useCallback(() => {
    subscriptionsRef.current.clear();
  }, []);
  return {
    publish,
    subscribe,
    unsubscribe,
    unsubscribeAll
  };
};